import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, UserPlus, Compass } from "lucide-react";
import api from "@/lib/api";
import { Avatar, LevelBadge, ProBadge } from "@/components/common";
import { PageHeader } from "@/components/PageHeader";
import { ConnectButton } from "@/components/ConnectButton";

function MatchCard({ m }) {
  const first = m.name.split(" ")[0];
  const prompts = [];
  (m.reasons || []).forEach((r) => {
    if (/^Can help you with: (.+)/i.test(r)) prompts.push(`Hi ${first} — I'm keen to learn more about ${r.split(": ")[1]}. Could I pick your brain sometime?`);
    else if (/^You can help them with: (.+)/i.test(r)) prompts.push(`Hi ${first} — I work a lot with ${r.split(": ")[1]} and happy to share what I've learned. Let's connect!`);
    else if (/^Shared expertise: (.+)/i.test(r)) prompts.push(`Hi ${first} — fellow ${r.split(": ")[1]} person here. Would love to compare notes.`);
  });
  if (m.company_stage) prompts.push(`Hi ${first} — we're both navigating the ${m.company_stage} stage. Would be great to swap experiences.`);
  prompts.push(`Hi ${first} — really impressed by your work. Would love to connect on Ikigora.`);
  const suggested = prompts[0] || "";
  return (
    <div className={`border rounded-xl p-5 flex flex-col fade-up ${m.is_new ? "border-cobalt shadow-sm" : "border-border"} bg-card`} data-testid={`match-${m.user_id}`}>
      {m.is_new && <span className="self-start mb-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-cobalt text-white rounded-full px-2 py-0.5" data-testid={`match-new-${m.user_id}`}><Sparkles size={10} /> New this week</span>}
      <div className="flex items-start gap-3">
        <Link to={`/members/${m.user_id}`}>
          <Avatar src={m.picture} name={m.name} size={52} className="border" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Link to={`/members/${m.user_id}`} className="font-semibold hover:text-cobalt transition-colors truncate">{m.name}</Link>
            {m.founding_member ? <ProBadge founding /> : m.plan === "pro" && <ProBadge />}
          </div>
          <div className="text-xs text-muted-foreground truncate">{(m.role?.length ? m.role.join(", ") : "") || m.title || "Member"}</div>
          <div className="mt-1 flex items-center gap-2">
            <LevelBadge level={m.level} />
            {m.industry?.length > 0 && <span className="text-[11px] text-muted-foreground truncate">{m.industry.join(", ")}</span>}
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-mono text-cobalt bg-secondary rounded-full px-2 py-1 shrink-0"
          data-testid={`match-score-${m.user_id}`} title="Match score">
          <Sparkles size={12} /> {m.score}
        </span>
      </div>

      {m.reasons?.length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid={`match-reasons-${m.user_id}`}>
          {m.reasons.map((r, i) => (
            <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
              <span className="mt-1 w-1 h-1 rounded-full bg-cobalt shrink-0" /> {r}
            </li>
          ))}
        </ul>
      )}

      {m.shared_expertise?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {m.shared_expertise.slice(0, 4).map((t) => (
            <span key={t} className="text-[11px] text-cobalt bg-secondary rounded-full px-2 py-0.5">{t}</span>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <ConnectButton userId={m.user_id} name={m.name} suggestedNote={suggested} prompts={prompts.slice(0, 3)} />
        <Link to={`/members/${m.user_id}`} data-testid={`match-view-${m.user_id}`}
          className="inline-flex items-center justify-center gap-1.5 border border-border rounded-md px-4 py-2 text-sm hover:border-cobalt hover:text-cobalt transition-colors">
          <UserPlus size={14} /> Profile
        </Link>
      </div>
    </div>
  );
}

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [onboarded, setOnboarded] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/matches").then((r) => {
      setMatches(r.data.matches || []);
      setOnboarded(r.data.onboarded);
      if (r.data.weekly_refresh) api.post("/matches/ack").catch(() => {});
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Discover" title="People you should meet"
        subtitle="Matched by expertise, what you want to learn, and shared goals." />

      {loading ? (
        <div className="text-center py-16 text-muted-foreground eyebrow">Finding your people…</div>
      ) : !onboarded ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl" data-testid="matches-onboard-prompt">
          <Compass size={28} className="mx-auto text-cobalt mb-3" />
          <p className="text-sm text-muted-foreground mb-4">Tell us about your profession and interests so we can match you.</p>
          <Link to="/onboarding" className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-gold-hover transition-colors">
            Complete your profile
          </Link>
        </div>
      ) : matches.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl text-muted-foreground" data-testid="matches-empty">
          No strong matches yet — check back as more members join and complete their profiles.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {matches.map((m) => <MatchCard key={m.user_id} m={m} />)}
        </div>
      )}
    </div>
  );
}
